import { Injectable } from '@angular/core';
import {
    AddressObject,
    BasicInfoObject,
    CenterObject,
    CreateAddressInput,
    CreateBasicInfoInput,
    CreateOrganizationInput,
    DeleteOrganizationInput,
    GetOrganizationInput,
    OkObject,
    OrganizationObject,
    OrganizationsGridObject,
    UpdateAddressInput,
    UpdateBasicInfoInput,
    UpdateCenterInput,
} from '@it2go/types';
import { GqlQueryOptions, GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { GridInput } from '@it2go/data-grid';
import { filterFrag } from './types/filter.types';
import { basicInfoFrag, centerFrag, organizationFrag, organizationGridItemFrag } from './types/organization.types';
import {
    OrganizationSettingsGridObject,
    OrganizationSettingsObject,
} from '@libs/types/src/organization/object-type/organization/organization-settings.object';
import {
    UserOrganizationAccessInput,
} from '@libs/types/src/organization/input-type/user/user-organization-access.input';
import {
    OrganizationSettingsGetInput,
} from '@libs/types/src/organization/input-type/organization/organization-settings-get.input';
import { organizationSettingsFrag } from './types/organization-settings.types';
import {
    OrganizationSettingsImportInput,
} from '@libs/types/src/organization/input-type/organization/organization-settings-import.input';
import { addressFrag } from './types/common.types';
import {
    OrganizationSettingsUpdateInput,
} from '@libs/types/src/organization/input-type/organization/organization-settings-update.input';

@Injectable({ providedIn: 'root' })
export class OrganizationGqlService extends GqlServiceV2 {

    public createOrganization(input: CreateOrganizationInput, fragment: string) {
        return this.mutate<OrganizationObject>(
            `
                mutation ($input: CreateOrganizationInput!) {
                    organization {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteOrganization(input: DeleteOrganizationInput, fragment: string = 'ok') {
        return this.mutate<OrganizationObject>(
            `
                mutation ($input: DeleteOrganizationInput!) {
                    organization {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getOrganization(input: GetOrganizationInput) {
        return this.query<OrganizationObject>(`
                query($input: GetOrganizationInput!) {
                    organization {
                        get(input: $input) {
                            ${organizationFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    getOrganizations(input: GridInput | null = null, frag: string = organizationGridItemFrag, options: GqlQueryOptions = {}) {
        return this.query<OrganizationsGridObject>(
            `
                query($input: GridInput) {
                    organization {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
            options,
        );
    }

    public getOrganizationSettings(input: GridInput | null, fragment: string, options: GqlQueryOptions = {}) {
        return this.query<OrganizationSettingsGridObject>(`
                query($input: GridInput) {
                    organizationSettings {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
            options,
        );
    }

    public updateOrganizationSetting(input: OrganizationSettingsUpdateInput, fragment: string = organizationSettingsFrag) {
        return this.mutate<OrganizationSettingsObject>(`
                mutation($input: OrganizationSettingsUpdateInput!) {
                    organizationSettings {
                        update(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public removeOrganizationSetting(input: OrganizationSettingsGetInput) {
        return this.mutate<OkObject>(`
                mutation($input: OrganizationSettingsGetInput!) {
                    organizationSettings {
                        remove(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public importOrganizationSettings(input: OrganizationSettingsImportInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: OrganizationSettingsImportInput!) {
                    organizationSettings {
                        importCsv(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public giveAccess(input: UserOrganizationAccessInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: UserOrganizationAccessInput!) {
                    organizationUser {
                        giveAccess(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getOrCreateOrganizationSetting(input: OrganizationSettingsGetInput) {
        return this.query<OrganizationSettingsObject>(`
                query($input: OrganizationSettingsGetInput!) {
                    organizationSettings {
                        getOrCreate(input: $input) {
                            ${organizationSettingsFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createBasicInfo(input: CreateBasicInfoInput, fragment = basicInfoFrag) {
        return this.mutate<BasicInfoObject>(
            `
                mutation($input: CreateBasicInfoInput!) {
                    basicInfo {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateBasicInfo(input: UpdateBasicInfoInput, fragment = basicInfoFrag) {
        return this.mutate<BasicInfoObject>(
            `
                mutation($input: UpdateBasicInfoInput!) {
                    basicInfo {
                        update(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteBasicInfo(id: number, fragment = 'ok') {
        return this.mutate(`
            mutation {
                basicInfo {
                    delete(input: {
                        id: ${id}
                    }) {
                        ${fragment}
                    }
                }
            }
        `);
    }

    public createCenter(organizationId: number, fragment = centerFrag) {
        return this.mutate<CenterObject>(`
                mutation {
                    center {
                        create(input: {
                            organizationId: ${organizationId},
                            addressIds: []
                            basicInfoIds: []
                            contactPersonIds: []
                        }) {
                            ${fragment}
                        }
                    }
                }
            `);
    }

    public updateCenter(input: UpdateCenterInput, fragment = 'ok') {
        return this.mutate<OkObject>(`
                mutation($input: UpdateCenterInput!) {
                    center {
                        update(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteCenter(id: number, fragment = 'ok') {
        return this.mutate<OkObject>(`
                mutation {
                    center {
                        delete(input: {
                            id: ${id}
                        }) {
                            ${fragment}
                        }
                    }
                }
            `);
    }


    public createAddress(input: CreateAddressInput, fragment = addressFrag): Promise<AddressObject> {
        return this.mutate<AddressObject>(`
                mutation ($input: CreateAddressInput!) {
                    address {
                        create(input: $input) {
                           note
                           validFrom
                           address {
                            ${fragment}
                           }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateAddress(input: UpdateAddressInput, fragment = addressFrag): Promise<AddressObject> {
        return this.mutate<AddressObject>(`
                mutation ($input: UpdateAddressInput!) {
                    address {
                        update(input: $input) {
                           note
                           validFrom
                           address {
                            ${fragment}
                           }
                       }
                    }
                }
            `,
            { input },
        );
    }

    public deleteAddress(id: number, fragment = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(`
                mutation {
                    address {
                        delete(input: {
                            id: ${id}
                        }) {
                            ${fragment}
                        }
                    }
                }
            `);
    }
}
